import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Apartment,
  AttachMoney,
  BusinessCenter,
  Construction,
  Delete,
  Edit,
  Gavel,
  MoreVert,
  People,
  Warning,
} from "@mui/icons-material";
import {
  useGetProjectsQuery,
  useGetPortfolioQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  ProTrackProject,
  ContractStage,
  ConstructionStage,
  FinancialStage,
  SalesStage,
  DebtorsStage,
  ConveyancingStage,
  ProjectSite,
  ProjectOverallStatus,
} from "../../../redux/protrackApi";

const NAVY = "#123456";
const TODAY = new Date().toISOString().slice(0, 10);

// ─── Sites ────────────────────────────────────────────────────────────────────

const SITES: { value: ProjectSite; label: string }[] = [
  { value: "MH", label: "MH — Marlborough Heights" },
  { value: "POMONA", label: "Pomona — Pomona Estate" },
  { value: "PRET1", label: "PRET1 — Pretoria Phase 1" },
  { value: "PRET2", label: "PRET2 — Pretoria Phase 2" },
];

// ─── RAG status config ────────────────────────────────────────────────────────

type RagColor = "success" | "warning" | "error" | "default";

const CONTRACT_RAG: Record<string, RagColor> = {
  DRAFT: "default",
  PENDING_SIGN: "warning",
  ACTIVE: "success",
  AMENDED: "warning",
  TERMINATED: "error",
};
const CONSTRUCTION_RAG: Record<string, RagColor> = {
  NOT_STARTED: "default",
  IN_PROGRESS: "warning",
  ON_HOLD: "warning",
  DELAYED: "error",
  PRACTICAL_COMPLETION: "success",
  COMPLETE: "success",
};
const FINANCIAL_RAG: Record<string, RagColor> = {
  CURRENT: "success",
  PAYMENT_DUE: "warning",
  OVERDUE: "error",
  IN_DISPUTE: "error",
  SETTLED: "success",
};
const SALES_RAG: Record<string, RagColor> = {
  PRE_LAUNCH: "default",
  SELLING: "success",
  SOLD_OUT: "success",
  SLOW_MOVING: "error",
};
const DEBTORS_RAG: Record<string, RagColor> = {
  CURRENT: "success",
  "30_DAYS": "warning",
  "60_DAYS": "error",
  "90_DAYS_PLUS": "error",
  LEGAL_ACTION: "error",
  WRITTEN_OFF: "error",
};
const CONVEYANCING_RAG: Record<string, RagColor> = {
  NOT_INITIATED: "default",
  DOCS_GATHERING: "warning",
  LODGED: "warning",
  IN_DEEDS_OFFICE: "warning",
  REGISTERED: "success",
  COMPLETE: "success",
};

function ragChip(
  status: string | undefined,
  map: Record<string, RagColor>,
  label?: string,
) {
  if (!status) return <Chip label="—" size="small" />;
  const color = map[status] ?? "default";
  const display = label ?? status.replace(/_/g, " ");
  return (
    <Chip
      label={display}
      size="small"
      color={color}
      sx={{ fontWeight: 600, fontSize: 10, height: 22 }}
    />
  );
}

// ─── Stage labels ─────────────────────────────────────────────────────────────

const CONTRACT_STATUSES = [
  "DRAFT",
  "PENDING_SIGN",
  "ACTIVE",
  "AMENDED",
  "TERMINATED",
];
const CONSTRUCTION_STATUSES = [
  "NOT_STARTED",
  "IN_PROGRESS",
  "ON_HOLD",
  "DELAYED",
  "PRACTICAL_COMPLETION",
  "COMPLETE",
];
const FINANCIAL_STATUSES = [
  "CURRENT",
  "PAYMENT_DUE",
  "OVERDUE",
  "IN_DISPUTE",
  "SETTLED",
];
const SALES_STATUSES = ["PRE_LAUNCH", "SELLING", "SOLD_OUT", "SLOW_MOVING"];
const DEBTORS_STATUSES = [
  "CURRENT",
  "30_DAYS",
  "60_DAYS",
  "90_DAYS_PLUS",
  "LEGAL_ACTION",
  "WRITTEN_OFF",
];
const CONVEYANCING_STATUSES = [
  "NOT_INITIATED",
  "DOCS_GATHERING",
  "LODGED",
  "IN_DEEDS_OFFICE",
  "REGISTERED",
  "COMPLETE",
];

// ─── Form default ─────────────────────────────────────────────────────────────

const emptyProject = (): Omit<
  ProTrackProject,
  "id" | "createdAt" | "updatedAt"
> => ({
  projectNumber: "",
  name: "",
  siteId: "MH",
  overallStatus: "ACTIVE",
  contract: { status: "ACTIVE", startDate: TODAY },
  construction: {
    status: "NOT_STARTED",
    costToCompletion: 0,
    workDoneToDate: 0,
  },
  financial: {
    status: "CURRENT",
    outstandingPayment: 0,
    cashOnHand: 0,
    nostroOnHand: 0,
    monthlyExpectedPayments: 0,
  },
  sales: {
    status: "PRE_LAUNCH",
    totalSalesToDate: 0,
    totalUnits: 0,
    unitsSold: 0,
    unsoldUnitsCount: 0,
  },
  debtors: {
    status: "CURRENT",
    debtorsPaidToDate: 0,
    outstandingDebtors: 0,
    monthlyExpectedCollections: 0,
  },
  conveyancing: {
    status: "NOT_INITIATED",
    unitsRegistered: 0,
    unitsPendingTransfer: 0,
  },
  notes: "",
});

// ─── Number field helper ──────────────────────────────────────────────────────

function numFmt(v?: number) {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 0 }).format(v);
}

// ─── Stage Tab Panel ─────────────────────────────────────────────────────────

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}

// ─── Portfolio Summary Bar ────────────────────────────────────────────────────

function PortfolioSummary() {
  const { data } = useGetPortfolioQuery();
  if (!data) return null;
  const { totals, sites } = data;
  return (
    <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
      {[
        { label: "Active Projects", value: totals.projects, color: NAVY },
        {
          label: "Total CTC",
          value: `$${numFmt(totals.totalCtc)}`,
          color: "#1565c0",
        },
        {
          label: "Work Done",
          value: `$${numFmt(totals.totalWorkDone)}`,
          color: "#2e7d32",
        },
        {
          label: "Total Sales",
          value: `$${numFmt(totals.totalSales)}`,
          color: "#2e7d32",
        },
        {
          label: "Outstanding Debtors",
          value: `$${numFmt(totals.totalOutstandingDebtors)}`,
          color:
            totals.totalOutstandingDebtors > 0 ? "#c62828" : "text.primary",
        },
        {
          label: "NOSTRO (USD)",
          value: `$${numFmt(totals.totalNostroUsd)}`,
          color: "#1565c0",
        },
        {
          label: "Cash on Hand",
          value: `$${numFmt(totals.totalCashOnHand)}`,
          color: "#2e7d32",
        },
      ].map((s) => (
        <Box
          key={s.label}
          sx={{
            flex: "1 1 130px",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 1.5,
            bgcolor: "#fff",
            minWidth: 120,
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {s.label}
          </Typography>
          <Typography fontWeight={700} color={s.color} fontSize={16}>
            {s.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Project Form Dialog ──────────────────────────────────────────────────────

interface ProjectFormProps {
  open: boolean;
  initial: Partial<ProTrackProject>;
  onClose: () => void;
  onSave: (p: Partial<ProTrackProject>) => Promise<void>;
  saving: boolean;
  error: string;
}

function ProjectForm({
  open,
  initial,
  onClose,
  onSave,
  saving,
  error,
}: ProjectFormProps) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<Partial<ProTrackProject>>(initial);

  // Sync initial when dialog opens with new data
  React.useEffect(() => {
    if (open) {
      setForm(initial);
      setTab(0);
    }
  }, [open, initial]);

  const set = (field: keyof ProTrackProject, value: unknown) =>
    setForm((f) => ({ ...f, [field]: value }));

  const setStage = <
    K extends
      | "contract"
      | "construction"
      | "financial"
      | "sales"
      | "debtors"
      | "conveyancing",
  >(
    stage: K,
    field: string,
    value: unknown,
  ) =>
    setForm((f) => ({
      ...f,
      [stage]: { ...(f[stage] as object), [field]: value },
    }));

  const numField = (
    stage:
      | "contract"
      | "construction"
      | "financial"
      | "sales"
      | "debtors"
      | "conveyancing",
    field: string,
    label: string,
    stageObj: Record<string, unknown> | undefined,
  ) => (
    <TextField
      size="small"
      label={label}
      type="number"
      value={(stageObj as Record<string, unknown>)?.[field] ?? ""}
      onChange={(e) =>
        setStage(
          stage,
          field,
          e.target.value === "" ? undefined : Number(e.target.value),
        )
      }
      inputProps={{ min: 0 }}
      sx={{ flex: "1 1 160px" }}
    />
  );

  const dateField = (
    stage:
      | "contract"
      | "construction"
      | "financial"
      | "sales"
      | "debtors"
      | "conveyancing",
    field: string,
    label: string,
    stageObj: Record<string, unknown> | undefined,
  ) => (
    <TextField
      size="small"
      label={label}
      type="date"
      value={(stageObj as Record<string, unknown>)?.[field] ?? ""}
      onChange={(e) => setStage(stage, field, e.target.value || undefined)}
      InputLabelProps={{ shrink: true }}
      sx={{ flex: "1 1 160px" }}
    />
  );

  const statusSelect = (
    stage:
      | "contract"
      | "construction"
      | "financial"
      | "sales"
      | "debtors"
      | "conveyancing",
    statuses: string[],
    stageObj: Record<string, unknown> | undefined,
  ) => (
    <FormControl size="small" sx={{ flex: "1 1 200px" }}>
      <InputLabel>Status</InputLabel>
      <Select
        label="Status"
        value={(stageObj as Record<string, unknown>)?.status ?? ""}
        onChange={(e) => setStage(stage, "status", e.target.value)}
      >
        {statuses.map((s) => (
          <MenuItem key={s} value={s}>
            {s.replace(/_/g, " ")}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const c = form.contract as ContractStage | undefined;
  const cn = form.construction as ConstructionStage | undefined;
  const fi = form.financial as FinancialStage | undefined;
  const sa = form.sales as SalesStage | undefined;
  const de = form.debtors as DebtorsStage | undefined;
  const co = form.conveyancing as ConveyancingStage | undefined;

  // Auto-compute pct for display only
  const ctc = cn?.costToCompletion ?? 0;
  const done = cn?.workDoneToDate ?? 0;
  const pct = ctc > 0 ? Math.round((done / ctc) * 100) : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: NAVY, color: "#fff", py: 2 }}>
        {initial.id ? `Edit — ${initial.name}` : "New CTC Project"}
      </DialogTitle>

      {/* Stage tabs */}
      <Box sx={{ borderBottom: "1px solid #e0e0e0", bgcolor: "#fafafa" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: 12,
              minHeight: 42,
            },
          }}
        >
          <Tab label="1 · Basic" />
          <Tab label="2 · Contract" />
          <Tab label="3 · Construction" />
          <Tab label="4 · Financial" />
          <Tab label="5 · Sales" />
          <Tab label="6 · Debtors" />
          <Tab label="7 · Conveyancing" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Tab 0: Basic Info ── */}
        <TabPanel value={tab} index={0}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <TextField
              size="small"
              label="Project Number (N/N)"
              required
              value={form.projectNumber ?? ""}
              onChange={(e) => set("projectNumber", e.target.value)}
              sx={{ flex: "1 1 120px" }}
            />
            <TextField
              size="small"
              label="Project Name"
              required
              value={form.name ?? ""}
              onChange={(e) => set("name", e.target.value)}
              sx={{ flex: "1 1 260px" }}
            />
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Site</InputLabel>
              <Select
                label="Site"
                value={form.siteId ?? "MH"}
                onChange={(e) => set("siteId", e.target.value as ProjectSite)}
              >
                {SITES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 140px" }}>
              <InputLabel>Overall Status</InputLabel>
              <Select
                label="Overall Status"
                value={form.overallStatus ?? "ACTIVE"}
                onChange={(e) =>
                  set("overallStatus", e.target.value as ProjectOverallStatus)
                }
              >
                {["ACTIVE", "ON_HOLD", "CLOSED"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Notes"
              multiline
              rows={2}
              value={form.notes ?? ""}
              onChange={(e) => set("notes", e.target.value)}
              sx={{ flex: "1 1 100%", mt: 1 }}
            />
          </Box>
        </TabPanel>

        {/* ── Tab 1: Contract ── */}
        <TabPanel value={tab} index={1}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {statusSelect(
              "contract",
              CONTRACT_STATUSES,
              c as Record<string, unknown>,
            )}
            {dateField(
              "contract",
              "startDate",
              "Contract Start Date",
              c as Record<string, unknown>,
            )}
            {dateField(
              "contract",
              "estimatedCompletionDate",
              "Est. Completion Date",
              c as Record<string, unknown>,
            )}
            {numField(
              "contract",
              "contractValue",
              "Contract Value (ZWL/ZiG)",
              c as Record<string, unknown>,
            )}
            {numField(
              "contract",
              "contractValueUsd",
              "Contract Value (USD)",
              c as Record<string, unknown>,
            )}
            <TextField
              size="small"
              label="Contractor Name"
              value={c?.contractorName ?? ""}
              onChange={(e) =>
                setStage("contract", "contractorName", e.target.value)
              }
              sx={{ flex: "1 1 200px" }}
            />
            <TextField
              size="small"
              label="Contractor Contact"
              value={c?.contractorContact ?? ""}
              onChange={(e) =>
                setStage("contract", "contractorContact", e.target.value)
              }
              sx={{ flex: "1 1 200px" }}
            />
          </Box>
        </TabPanel>

        {/* ── Tab 2: Construction ── */}
        <TabPanel value={tab} index={2}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {statusSelect(
              "construction",
              CONSTRUCTION_STATUSES,
              cn as Record<string, unknown>,
            )}
            {numField(
              "construction",
              "costToCompletion",
              "Cost to Completion (CTC)",
              cn as Record<string, unknown>,
            )}
            {numField(
              "construction",
              "workDoneToDate",
              "Work Done to Date",
              cn as Record<string, unknown>,
            )}
            <Box flex="1 1 100%">
              <Typography variant="caption" color="text.secondary">
                Balance to Finish:{" "}
                <strong>{numFmt(Math.max(0, ctc - done))}</strong> &nbsp;|&nbsp;
                Progress: <strong>{pct}%</strong>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  mt: 0.5,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": { bgcolor: NAVY },
                }}
              />
            </Box>
            {dateField(
              "construction",
              "plannedCompletion",
              "Planned Completion",
              cn as Record<string, unknown>,
            )}
            {dateField(
              "construction",
              "actualCompletion",
              "Actual Completion",
              cn as Record<string, unknown>,
            )}
          </Box>
        </TabPanel>

        {/* ── Tab 3: Financial ── */}
        <TabPanel value={tab} index={3}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {statusSelect(
              "financial",
              FINANCIAL_STATUSES,
              fi as Record<string, unknown>,
            )}
            {dateField(
              "financial",
              "lastPaymentDate",
              "Last Payment Date",
              fi as Record<string, unknown>,
            )}
            {numField(
              "financial",
              "outstandingPayment",
              "Outstanding Payment",
              fi as Record<string, unknown>,
            )}
            {numField(
              "financial",
              "cashOnHand",
              "Cash on Hand (ZWL/ZiG)",
              fi as Record<string, unknown>,
            )}
            {numField(
              "financial",
              "nostroOnHand",
              "NOSTRO on Hand (USD)",
              fi as Record<string, unknown>,
            )}
            {numField(
              "financial",
              "monthlyExpectedPayments",
              "Monthly Expected Payments",
              fi as Record<string, unknown>,
            )}
          </Box>
        </TabPanel>

        {/* ── Tab 4: Sales ── */}
        <TabPanel value={tab} index={4}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {statusSelect(
              "sales",
              SALES_STATUSES,
              sa as Record<string, unknown>,
            )}
            {numField(
              "sales",
              "totalUnits",
              "Total Units",
              sa as Record<string, unknown>,
            )}
            {numField(
              "sales",
              "unitsSold",
              "Units Sold",
              sa as Record<string, unknown>,
            )}
            {numField(
              "sales",
              "unsoldUnitsCount",
              "Unsold Units",
              sa as Record<string, unknown>,
            )}
            {numField(
              "sales",
              "totalSalesToDate",
              "Total Sales Value",
              sa as Record<string, unknown>,
            )}
            {numField(
              "sales",
              "unsoldStockValue",
              "Unsold Stock Value",
              sa as Record<string, unknown>,
            )}
            {numField(
              "sales",
              "salesVelocityPerMonth",
              "Sales Velocity (units/month)",
              sa as Record<string, unknown>,
            )}
          </Box>
        </TabPanel>

        {/* ── Tab 5: Debtors ── */}
        <TabPanel value={tab} index={5}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {statusSelect(
              "debtors",
              DEBTORS_STATUSES,
              de as Record<string, unknown>,
            )}
            {numField(
              "debtors",
              "debtorsPaidToDate",
              "Debtors Paid to Date",
              de as Record<string, unknown>,
            )}
            {numField(
              "debtors",
              "outstandingDebtors",
              "Outstanding Debtors",
              de as Record<string, unknown>,
            )}
            {dateField(
              "debtors",
              "lastPaymentDate",
              "Last Buyer Payment Date",
              de as Record<string, unknown>,
            )}
            {numField(
              "debtors",
              "monthlyExpectedCollections",
              "Monthly Expected Collections",
              de as Record<string, unknown>,
            )}
            {numField(
              "debtors",
              "daysOutstanding",
              "Days Outstanding (DSO)",
              de as Record<string, unknown>,
            )}
          </Box>
        </TabPanel>

        {/* ── Tab 6: Conveyancing ── */}
        <TabPanel value={tab} index={6}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {statusSelect(
              "conveyancing",
              CONVEYANCING_STATUSES,
              co as Record<string, unknown>,
            )}
            {dateField(
              "conveyancing",
              "startDate",
              "Conveyancing Start Date",
              co as Record<string, unknown>,
            )}
            {dateField(
              "conveyancing",
              "completionDate",
              "Registration/Completion Date",
              co as Record<string, unknown>,
            )}
            <TextField
              size="small"
              label="Attorney / Firm"
              value={co?.attorney ?? ""}
              onChange={(e) =>
                setStage("conveyancing", "attorney", e.target.value)
              }
              sx={{ flex: "1 1 220px" }}
            />
            {numField(
              "conveyancing",
              "unitsRegistered",
              "Units Registered",
              co as Record<string, unknown>,
            )}
            {numField(
              "conveyancing",
              "unitsPendingTransfer",
              "Units Pending Transfer",
              co as Record<string, unknown>,
            )}
          </Box>
        </TabPanel>

        {error && (
          <Typography color="error" variant="caption" mt={2} display="block">
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={saving || !form.projectNumber?.trim() || !form.name?.trim()}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save Project"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Project Row ──────────────────────────────────────────────────────────────

interface ProjectRowProps {
  project: ProTrackProject;
  onEdit: (p: ProTrackProject) => void;
  onDelete: (p: ProTrackProject) => void;
}

function ProjectRow({ project, onEdit, onDelete }: ProjectRowProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const p = project;
  const pct = p.construction?.percentComplete ?? 0;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 60px repeat(6, 1fr) 40px",
        alignItems: "center",
        gap: 1,
        py: 1.5,
        px: 2,
        borderBottom: "1px solid #f0f0f0",
        bgcolor: "#fff",
        "&:hover": { bgcolor: "#f8f9fc" },
        transition: "background 0.15s",
      }}
    >
      {/* Name + site */}
      <Box>
        <Typography fontWeight={600} fontSize={13} color={NAVY}>
          {p.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {p.siteId} &bull; #{p.projectNumber}
        </Typography>
        {pct > 0 && (
          <Tooltip title={`Construction ${pct}% complete`}>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                mt: 0.5,
                height: 4,
                borderRadius: 2,
                bgcolor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  bgcolor:
                    CONSTRUCTION_RAG[p.construction?.status ?? ""] === "error"
                      ? "#c62828"
                      : NAVY,
                },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Overall status */}
      <Chip
        label={p.overallStatus}
        size="small"
        color={
          p.overallStatus === "ACTIVE"
            ? "success"
            : p.overallStatus === "ON_HOLD"
            ? "warning"
            : "default"
        }
        sx={{ fontWeight: 600, fontSize: 10, height: 22 }}
      />

      {/* 6 stage RAG chips */}
      {ragChip(p.contract?.status, CONTRACT_RAG)}
      {ragChip(p.construction?.status, CONSTRUCTION_RAG)}
      {ragChip(p.financial?.status, FINANCIAL_RAG)}
      {ragChip(p.sales?.status, SALES_RAG)}
      {ragChip(p.debtors?.status, DEBTORS_RAG)}
      {ragChip(p.conveyancing?.status, CONVEYANCING_RAG)}

      {/* Actions */}
      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onEdit(p);
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onDelete(p);
          }}
          sx={{ color: "error.main" }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const ProTrackProjects: React.FC = () => {
  const [siteFilter, setSiteFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { data: projects = [], isLoading } = useGetProjectsQuery({
    siteId: siteFilter || undefined,
    overallStatus: statusFilter || undefined,
  });

  const [createProject, { isLoading: creating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: updating }] = useUpdateProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProTrackProject | null>(null);
  const [formError, setFormError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ProTrackProject | null>(
    null,
  );

  const formInitial = useMemo(
    () => editTarget ?? (emptyProject() as Partial<ProTrackProject>),
    [editTarget, formOpen], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const openCreate = () => {
    setEditTarget(null);
    setFormError("");
    setFormOpen(true);
  };
  const openEdit = (p: ProTrackProject) => {
    setEditTarget(p);
    setFormError("");
    setFormOpen(true);
  };

  const handleSave = async (form: Partial<ProTrackProject>) => {
    if (!form.projectNumber?.trim() || !form.name?.trim()) return;
    setFormError("");
    try {
      if (editTarget?.id) {
        await updateProject({ id: editTarget.id, data: form }).unwrap();
      } else {
        await createProject(
          form as Omit<ProTrackProject, "id" | "createdAt" | "updatedAt">,
        ).unwrap();
      }
      setFormOpen(false);
    } catch (err: unknown) {
      const e = err as { data?: { error?: { message?: string } } };
      setFormError(
        e?.data?.error?.message ?? "Failed to save. Please try again.",
      );
    }
  };

  const handleDelete = async () => {
    if (deleteTarget?.id) await deleteProject(deleteTarget.id);
    setDeleteTarget(null);
  };

  // Group projects by site for display
  const grouped = useMemo(() => {
    const g: Record<string, ProTrackProject[]> = {};
    for (const p of projects) {
      (g[p.siteId] = g[p.siteId] ?? []).push(p);
    }
    return g;
  }, [projects]);

  const siteLabels: Record<string, string> = {
    MH: "Marlborough Heights",
    POMONA: "Pomona Estate",
    PRET1: "Pretoria Phase 1",
    PRET2: "Pretoria Phase 2",
  };

  const stageIcons = [
    <BusinessCenter sx={{ fontSize: 13 }} />,
    <Construction sx={{ fontSize: 13 }} />,
    <AttachMoney sx={{ fontSize: 13 }} />,
    <Apartment sx={{ fontSize: 13 }} />,
    <People sx={{ fontSize: 13 }} />,
    <Gavel sx={{ fontSize: 13 }} />,
  ];
  const stageLabels = [
    "Contract",
    "Construction",
    "Financial",
    "Sales",
    "Debtors",
    "Conveyancing",
  ];

  return (
    <Box>
      {/* Portfolio summary */}
      <PortfolioSummary />

      {/* Toolbar */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Site</InputLabel>
            <Select
              label="Filter by Site"
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
            >
              <MenuItem value="">All Sites</MenuItem>
              {SITES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="ON_HOLD">On Hold</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
        >
          Add Project
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : projects.length === 0 ? (
        <Box textAlign="center" py={8} color="text.secondary">
          <BusinessCenter sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography mt={1}>
            No CTC projects found. Add the first project.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Table header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 60px repeat(6, 1fr) 40px",
              gap: 1,
              py: 1,
              px: 2,
              bgcolor: NAVY,
              borderRadius: "8px 8px 0 0",
              alignItems: "center",
            }}
          >
            <Typography variant="caption" fontWeight={700} color="#fff">
              Project
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              color="rgba(255,255,255,0.8)"
            >
              Status
            </Typography>
            {stageLabels.map((l, i) => (
              <Box key={l} display="flex" alignItems="center" gap={0.5}>
                <Box color="rgba(255,255,255,0.7)">{stageIcons[i]}</Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="rgba(255,255,255,0.9)"
                >
                  {l}
                </Typography>
              </Box>
            ))}
            <Box />
          </Box>

          {/* Rows grouped by site */}
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              overflow: "hidden",
            }}
          >
            {Object.entries(grouped).map(([site, siteProjects]) => (
              <Box key={site}>
                <Box sx={{ px: 2, py: 0.75, bgcolor: "#f0f4f8" }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={NAVY}
                    textTransform="uppercase"
                    letterSpacing={0.5}
                  >
                    {site} — {siteLabels[site] ?? site} ({siteProjects.length})
                  </Typography>
                </Box>
                {siteProjects.map((p) => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </Box>
            ))}
          </Box>

          {/* Legend */}
          <Box display="flex" gap={1.5} flexWrap="wrap" mt={2}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              RAG Legend:
            </Typography>
            {[
              { color: "success" as const, label: "On Track" },
              { color: "warning" as const, label: "Attention" },
              { color: "error" as const, label: "Critical" },
              { color: "default" as const, label: "Not Started" },
            ].map((l) => (
              <Box key={l.label} display="flex" alignItems="center" gap={0.5}>
                <Chip
                  label={l.label}
                  size="small"
                  color={l.color}
                  sx={{ height: 18, fontSize: 10 }}
                />
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Add/Edit Dialog */}
      <ProjectForm
        open={formOpen}
        initial={formInitial}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        saving={creating || updating}
        error={formError}
      />

      {/* Delete Confirm */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="error" /> Delete Project
        </DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteTarget?.name}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
